import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { useAdminAuth } from "@/context/Admin"
import { Loader2, Save } from "lucide-react"
import { toast } from "sonner"
import MediaSelectorModal from "../library/media-selector"

export default function AboutUsManagement() {
  const { getAboutUs, updateAboutUs } = useAdminAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false)
  const [mediaSelectorFor, setMediaSelectorFor] = useState(null)
  const [formData, setFormData] = useState({
    hero_title: "",
    hero_subtitle: "",
    hero_button_text: "",
    hero_button_link: "",
    hero_secondary_button_text: "",
    hero_secondary_button_link: "",
    stats: [{ number: "", label: "" }],
    story_title: "",
    story_content: "",
    story_image: "",
    story_award_title: "",
    story_award_subtitle: "",
    values_title: "",
    values_subtitle: "",
    values: [{ icon: "Leaf", title: "", description: "" }],
    team_title: "",
    team_subtitle: "",
    team: [{ name: "", role: "", image: "", bio: "", facebook: "", instagram: "", twitter: "", linkedin: "" }],
    mission_title: "",
    mission_content: "",
    cta_title: "",
    cta_subtitle: "",
    cta_button_text: "",
    cta_button_link: "",
    cta_secondary_button_text: "",
    cta_secondary_button_link: "",
  })

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const data = await getAboutUs()
        if (data) {
          setFormData({
            hero_title: data.hero_title || "",
            hero_subtitle: data.hero_subtitle || "",
            hero_button_text: data.hero_button_text || "",
            hero_button_link: data.hero_button_link || "",
            hero_secondary_button_text: data.hero_secondary_button_text || "",
            hero_secondary_button_link: data.hero_secondary_button_link || "",
            stats: data.stats && data.stats.length > 0 ? data.stats : [{ number: "", label: "" }],
            story_title: data.story_title || "",
            story_content: data.story_content || "",
            story_image: data.story_image || "",
            story_award_title: data.story_award_title || "",
            story_award_subtitle: data.story_award_subtitle || "",
            values_title: data.values_title || "",
            values_subtitle: data.values_subtitle || "",
            values: data.values && data.values.length > 0 ? data.values : [{ icon: "Leaf", title: "", description: "" }],
            team_title: data.team_title || "",
            team_subtitle: data.team_subtitle || "",
            team: data.team && data.team.length > 0 ? data.team.map(m => ({ ...m, facebook: m.facebook || "", instagram: m.instagram || "", twitter: m.twitter || "", linkedin: m.linkedin || "" })) : [{ name: "", role: "", image: "", bio: "", facebook: "", instagram: "", twitter: "", linkedin: "" }],
            mission_title: data.mission_title || "",
            mission_content: data.mission_content || "",
            cta_title: data.cta_title || "",
            cta_subtitle: data.cta_subtitle || "",
            cta_button_text: data.cta_button_text || "",
            cta_button_link: data.cta_button_link || "",
            cta_secondary_button_text: data.cta_secondary_button_text || "",
            cta_secondary_button_link: data.cta_secondary_button_link || "",
          })
        }
      } catch (error) {
        console.error("Error fetching about us:", error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [getAboutUs])

  const handleSave = async () => {
    setSaving(true)
    try {
      await updateAboutUs(formData)
      toast.success("About us content updated successfully!")
    } catch (error) {
      console.error("Error updating about us:", error)
    } finally {
      setSaving(false)
    }
  }

  const handleImageSelect = (imageUrl) => {
    if (mediaSelectorFor) {
      if (mediaSelectorFor.startsWith("team_")) {
        // Handle team member image selection
        const parts = mediaSelectorFor.split("_")
        const index = parseInt(parts[1])
        updateTeamMember(index, "image", imageUrl)
      } else {
        // Handle regular field
        setFormData((prev) => ({
          ...prev,
          [mediaSelectorFor]: imageUrl,
        }))
      }
    }
    setIsMediaModalOpen(false)
    setMediaSelectorFor(null)
  }

  const addStat = () => {
    setFormData((prev) => ({
      ...prev,
      stats: [...prev.stats, { number: "", label: "" }],
    }))
  }

  const removeStat = (index) => {
    setFormData((prev) => ({
      ...prev,
      stats: prev.stats.filter((_, i) => i !== index),
    }))
  }

  const updateStat = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      stats: prev.stats.map((stat, i) => (i === index ? { ...stat, [field]: value } : stat)),
    }))
  }

  const addValue = () => {
    setFormData((prev) => ({
      ...prev,
      values: [...prev.values, { icon: "Leaf", title: "", description: "" }],
    }))
  }

  const removeValue = (index) => {
    setFormData((prev) => ({
      ...prev,
      values: prev.values.filter((_, i) => i !== index),
    }))
  }

  const updateValue = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      values: prev.values.map((val, i) => (i === index ? { ...val, [field]: value } : val)),
    }))
  }

  const addTeamMember = () => {
    setFormData((prev) => ({
      ...prev,
      team: [...prev.team, { name: "", role: "", image: "", bio: "", facebook: "", instagram: "", twitter: "", linkedin: "" }],
    }))
  }

  const removeTeamMember = (index) => {
    setFormData((prev) => ({
      ...prev,
      team: prev.team.filter((_, i) => i !== index),
    }))
  }

  const updateTeamMember = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      team: prev.team.map((member, i) => (i === index ? { ...member, [field]: value } : member)),
    }))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">About Us Management</h1>
          <p className="text-gray-600 mt-1">Manage your about us page content</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Hero Section */}
      <Card>
        <CardHeader>
          <CardTitle>Hero Section</CardTitle>
          <CardDescription>Main hero section at the top of the page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Hero Title</Label>
            <Input
              value={formData.hero_title}
              onChange={(e) => setFormData({ ...formData, hero_title: e.target.value })}
              placeholder="Transforming Skin with Nature's Power"
            />
          </div>
          <div>
            <Label>Hero Subtitle</Label>
            <Textarea
              value={formData.hero_subtitle}
              onChange={(e) => setFormData({ ...formData, hero_subtitle: e.target.value })}
              placeholder="Founded in 2019..."
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Button Text</Label>
              <Input
                value={formData.hero_button_text}
                onChange={(e) => setFormData({ ...formData, hero_button_text: e.target.value })}
                placeholder="Shop Our Products"
              />
            </div>
            <div>
              <Label>Button Link</Label>
              <Input
                value={formData.hero_button_link}
                onChange={(e) => setFormData({ ...formData, hero_button_link: e.target.value })}
                placeholder="/shop"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Secondary Button Text</Label>
              <Input
                value={formData.hero_secondary_button_text}
                onChange={(e) => setFormData({ ...formData, hero_secondary_button_text: e.target.value })}
                placeholder="Watch Our Story"
              />
            </div>
            <div>
              <Label>Secondary Button Link</Label>
              <Input
                value={formData.hero_secondary_button_link}
                onChange={(e) => setFormData({ ...formData, hero_secondary_button_link: e.target.value })}
                placeholder="#"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Section */}
      <Card>
        <CardHeader>
          <CardTitle>Statistics</CardTitle>
          <CardDescription>Statistics displayed on the about page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.stats.map((stat, index) => (
            <div key={index} className="flex gap-4 items-end">
              <div className="flex-1">
                <Label>Number</Label>
                <Input
                  value={stat.number}
                  onChange={(e) => updateStat(index, "number", e.target.value)}
                  placeholder="500K+"
                />
              </div>
              <div className="flex-1">
                <Label>Label</Label>
                <Input
                  value={stat.label}
                  onChange={(e) => updateStat(index, "label", e.target.value)}
                  placeholder="Happy Customers"
                />
              </div>
              <Button
                variant="destructive"
                onClick={() => removeStat(index)}
                disabled={formData.stats.length === 1}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button variant="outline" onClick={addStat}>
            Add Stat
          </Button>
        </CardContent>
      </Card>

      {/* Story Section */}
      <Card>
        <CardHeader>
          <CardTitle>Our Story</CardTitle>
          <CardDescription>The story section with image and award</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Story Title</Label>
            <Input
              value={formData.story_title}
              onChange={(e) => setFormData({ ...formData, story_title: e.target.value })}
              placeholder="Our Story"
            />
          </div>
          <div>
            <Label>Story Content</Label>
            <Textarea
              value={formData.story_content}
              onChange={(e) => setFormData({ ...formData, story_content: e.target.value })}
              placeholder="Fayoz was born from..."
              rows={6}
            />
          </div>
          <div>
            <Label>Story Image</Label>
            <div className="flex gap-2">
              <Input
                value={formData.story_image}
                onChange={(e) => setFormData({ ...formData, story_image: e.target.value })}
                placeholder="/image.jpg"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setMediaSelectorFor("story_image")
                  setIsMediaModalOpen(true)
                }}
              >
                Select Image
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Award Title</Label>
              <Input
                value={formData.story_award_title}
                onChange={(e) => setFormData({ ...formData, story_award_title: e.target.value })}
                placeholder="Award Winning"
              />
            </div>
            <div>
              <Label>Award Subtitle</Label>
              <Input
                value={formData.story_award_subtitle}
                onChange={(e) => setFormData({ ...formData, story_award_subtitle: e.target.value })}
                placeholder="Best Natural Skincare 2023"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Values Section */}
      <Card>
        <CardHeader>
          <CardTitle>Our Values</CardTitle>
          <CardDescription>Core values displayed on the page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Values Title</Label>
            <Input
              value={formData.values_title}
              onChange={(e) => setFormData({ ...formData, values_title: e.target.value })}
              placeholder="Our Values"
            />
          </div>
          <div>
            <Label>Values Subtitle</Label>
            <Textarea
              value={formData.values_subtitle}
              onChange={(e) => setFormData({ ...formData, values_subtitle: e.target.value })}
              placeholder="These core principles..."
              rows={2}
            />
          </div>
          {formData.values.map((value, index) => (
            <div key={index} className="border p-4 rounded-lg space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Icon (Leaf, Shield, Heart, Globe)</Label>
                  <Input
                    value={value.icon}
                    onChange={(e) => updateValue(index, "icon", e.target.value)}
                    placeholder="Leaf"
                  />
                </div>
                <div>
                  <Label>Title</Label>
                  <Input
                    value={value.title}
                    onChange={(e) => updateValue(index, "title", e.target.value)}
                    placeholder="Natural Ingredients"
                  />
                </div>
                <div>
                  <Button
                    variant="destructive"
                    onClick={() => removeValue(index)}
                    disabled={formData.values.length === 1}
                  >
                    Remove
                  </Button>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={value.description}
                  onChange={(e) => updateValue(index, "description", e.target.value)}
                  placeholder="We source only..."
                  rows={2}
                />
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addValue}>
            Add Value
          </Button>
        </CardContent>
      </Card>

      {/* Team Section */}
      <Card>
        <CardHeader>
          <CardTitle>Team Members</CardTitle>
          <CardDescription>Team members displayed on the page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Team Title</Label>
            <Input
              value={formData.team_title}
              onChange={(e) => setFormData({ ...formData, team_title: e.target.value })}
              placeholder="Meet Our Team"
            />
          </div>
          <div>
            <Label>Team Subtitle</Label>
            <Textarea
              value={formData.team_subtitle}
              onChange={(e) => setFormData({ ...formData, team_subtitle: e.target.value })}
              placeholder="The passionate experts..."
              rows={2}
            />
          </div>
          {formData.team.map((member, index) => (
            <div key={index} className="border p-4 rounded-lg space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={member.name}
                    onChange={(e) => updateTeamMember(index, "name", e.target.value)}
                    placeholder="Dr. Sarah Chen"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <Input
                    value={member.role}
                    onChange={(e) => updateTeamMember(index, "role", e.target.value)}
                    placeholder="Founder & Chief Formulator"
                  />
                </div>
              </div>
              <div>
                <Label>Image URL</Label>
                <div className="flex gap-2">
                  <Input
                    value={member.image}
                    onChange={(e) => updateTeamMember(index, "image", e.target.value)}
                    placeholder="https://..."
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setMediaSelectorFor(`team_${index}_image`)
                      setIsMediaModalOpen(true)
                    }}
                  >
                    Select
                  </Button>
                </div>
              </div>
              <div>
                <Label>Bio</Label>
                <Input
                  value={member.bio}
                  onChange={(e) => updateTeamMember(index, "bio", e.target.value)}
                  placeholder="15+ years in dermatology research"
                />
              </div>
              <div className="border-t pt-4 mt-2">
                <Label className="text-sm text-gray-500 mb-3 block">Social Media Links (Optional)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Facebook URL</Label>
                    <Input
                      value={member.facebook}
                      onChange={(e) => updateTeamMember(index, "facebook", e.target.value)}
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <div>
                    <Label>Instagram URL</Label>
                    <Input
                      value={member.instagram}
                      onChange={(e) => updateTeamMember(index, "instagram", e.target.value)}
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                  <div>
                    <Label>Twitter (X) URL</Label>
                    <Input
                      value={member.twitter}
                      onChange={(e) => updateTeamMember(index, "twitter", e.target.value)}
                      placeholder="https://x.com/..."
                    />
                  </div>
                  <div>
                    <Label>LinkedIn URL</Label>
                    <Input
                      value={member.linkedin}
                      onChange={(e) => updateTeamMember(index, "linkedin", e.target.value)}
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                </div>
              </div>
              <Button
                variant="destructive"
                onClick={() => removeTeamMember(index)}
                disabled={formData.team.length === 1}
              >
                Remove Member
              </Button>
            </div>
          ))}
          <Button variant="outline" onClick={addTeamMember}>
            Add Team Member
          </Button>
        </CardContent>
      </Card>

      {/* Mission Section */}
      <Card>
        <CardHeader>
          <CardTitle>Mission Section</CardTitle>
          <CardDescription>The mission statement section</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Mission Title</Label>
            <Input
              value={formData.mission_title}
              onChange={(e) => setFormData({ ...formData, mission_title: e.target.value })}
              placeholder="Our Mission"
            />
          </div>
          <div>
            <Label>Mission Content</Label>
            <Textarea
              value={formData.mission_content}
              onChange={(e) => setFormData({ ...formData, mission_content: e.target.value })}
              placeholder="To empower everyone..."
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      {/* CTA Section */}
      <Card>
        <CardHeader>
          <CardTitle>Call to Action</CardTitle>
          <CardDescription>Final CTA section</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>CTA Title</Label>
            <Input
              value={formData.cta_title}
              onChange={(e) => setFormData({ ...formData, cta_title: e.target.value })}
              placeholder="Ready to Transform Your Skin?"
            />
          </div>
          <div>
            <Label>CTA Subtitle</Label>
            <Textarea
              value={formData.cta_subtitle}
              onChange={(e) => setFormData({ ...formData, cta_subtitle: e.target.value })}
              placeholder="Join thousands..."
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Button Text</Label>
              <Input
                value={formData.cta_button_text}
                onChange={(e) => setFormData({ ...formData, cta_button_text: e.target.value })}
                placeholder="Shop Now"
              />
            </div>
            <div>
              <Label>Button Link</Label>
              <Input
                value={formData.cta_button_link}
                onChange={(e) => setFormData({ ...formData, cta_button_link: e.target.value })}
                placeholder="/shop"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Secondary Button Text</Label>
              <Input
                value={formData.cta_secondary_button_text}
                onChange={(e) => setFormData({ ...formData, cta_secondary_button_text: e.target.value })}
                placeholder="Contact Us"
              />
            </div>
            <div>
              <Label>Secondary Button Link</Label>
              <Input
                value={formData.cta_secondary_button_link}
                onChange={(e) => setFormData({ ...formData, cta_secondary_button_link: e.target.value })}
                placeholder="/contact-us"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <MediaSelectorModal
        isOpen={isMediaModalOpen}
        onClose={() => {
          setIsMediaModalOpen(false)
          setMediaSelectorFor(null)
        }}
        onSelect={handleImageSelect}
      />
    </div>
  )
}

